import React, { ReactNode, useState } from 'react';
import { T } from '@transifex/react';
import { Link } from 'react-router-dom';
import { useCompany } from 'src/app/Company.store';
import { useSkipHireEnabled } from 'src/app/useSkipHireEnabled';
import {
  LanguagePickerModal,
  useHasMultipleLanguages,
} from 'src/components/login/LanguagePicker';
import { WasteHeroLogo1 } from 'src/svgs';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { cn } from '@/lib/utils';

export type LoginShellProps = {
  /** Main login content (address-select, MitID buttons, etc.). */
  children: ReactNode;
  /** Subtitle shown above the children (defaults to the address-prompt copy). */
  title?: ReactNode;
  /** Optional second-line description rendered under the subtitle. */
  description?: ReactNode;
  /**
   * Replaces the default footer link (the skip-hire request door, when the
   * tenant has it on). The door itself passes its way back to the login here;
   * `null` renders no link at all.
   */
  footerLink?: ReactNode;
  className?: string;
};

/** The footer utility link of the login surface (door link, language). */
export const LoginFooterLink: React.FC<{
  to: string;
  children: ReactNode;
  'data-testid'?: string;
}> = ({ to, children, 'data-testid': testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="relative -m-4 cursor-pointer p-4 uppercase tracking-[0.05em] text-[var(--text-link)] outline-hidden transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
  >
    {children}
  </Link>
);

export const LoginShell: React.FC<LoginShellProps> = ({
  children,
  title,
  description,
  footerLink,
  className,
}) => {
  const { data: company } = useCompany();
  const skipHireEnabled = useSkipHireEnabled();
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const hasMultipleLanguages = useHasMultipleLanguages();
  // The split-screen image only shows at lg+. Gate the render (not just CSS
  // `hidden`) so the background image isn't downloaded on mobile/tablet —
  // browsers fetch a background declared on a display:none element regardless.
  const isDesktop = useBreakpoint('lg');
  // "Upload image" background setting: when the tenant provides a login image we
  // split the screen — content on the left, image on the right (desktop only).
  // When unset ("Use Main colour setting") the layout stays single-column.
  const loginImage = company.background_image_for_login_page;
  // Escape for safe interpolation inside a double-quoted CSS url("…"): a literal
  // " or \ in the tenant-supplied URL would otherwise terminate the token and
  // silently drop the background. (React applies this via the CSSOM, so it stays
  // a single property value — but the token can still break without escaping.)
  const loginImageCss = loginImage
    ? `url("${loginImage.replace(/[\\"]/g, '\\$&').replace(/[\r\n]/g, '')}")`
    : undefined;

  const loginPageSettings = company.page_description_settings?.find(
    (setting) => setting?.page === 'login'
  );
  const hasTenantCopy =
    !!loginPageSettings?.title || !!loginPageSettings?.sections?.length;

  // 300ms ease-out fade-in + slide-up entrance, applied with stagger.
  // motion-reduce disables the animation; fill-mode-backwards preserves the
  // hidden initial state (opacity 0 + 8px offset) until each delay elapses.
  // Must stay `backwards`, not `both`/`forwards`: a forwards-filling
  // opacity/transform animation acts as a permanent will-change (Web
  // Animations §side-effects), so each animated block becomes a stacking
  // context forever — which painted the footer over the AddressSelect
  // dropdown despite its z-50.
  const enter =
    'animate-in fade-in-0 slide-in-from-bottom-2 duration-300 ease-out fill-mode-backwards motion-reduce:animate-none';
  // Same timing/fill behavior, no vertical movement — used for the footer so
  // it doesn't drift up as the page settles.
  const fadeEnter =
    'animate-in fade-in-0 duration-300 ease-out fill-mode-backwards motion-reduce:animate-none';

  return (
    <div className="wh-ui flex min-h-screen w-full bg-background">
      <div
        className={cn(
          'flex min-h-screen flex-1 flex-col items-center px-6 pb-8 pt-16',
          className
        )}
      >
        <section className="m-auto flex w-full max-w-[480px] flex-col items-center gap-12">
          {company.logo ? (
            <div
              className={cn('flex h-16 items-center justify-center', enter)}
              style={{ animationDelay: '0ms' }}
            >
              <img
                src={company.logo}
                alt={company.name}
                decoding="sync"
                fetchPriority="high"
                className="max-h-full max-w-[320px]"
              />
            </div>
          ) : (
            <WasteHeroLogo1
              style={{ fontSize: 48, animationDelay: '0ms' }}
              className={enter}
            />
          )}

          <div
            className={cn(
              'flex w-full max-w-[420px] flex-col gap-4 text-center',
              enter
            )}
            style={{ animationDelay: '80ms' }}
          >
            {title ? (
              <p className="text-xl font-normal leading-[1.5] text-foreground text-balance">
                {title}
              </p>
            ) : hasTenantCopy ? (
              <>
                {loginPageSettings?.title && (
                  <p className="text-xl font-normal leading-[1.5] text-foreground text-balance">
                    {loginPageSettings.title}
                  </p>
                )}
                {loginPageSettings?.sections?.map((section, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    {section.title && (
                      <p className="text-base font-medium leading-[1.5] text-foreground text-balance">
                        {section.title}
                      </p>
                    )}
                    {section.description && (
                      <p className="whitespace-pre-line text-xl leading-[1.5] text-muted-foreground">
                        {section.description}
                        {section.link_url && (
                          <>
                            {' '}
                            <a
                              href={section.link_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[var(--text-link)] underline-offset-4 hover:underline"
                            >
                              {section.link_label || section.link_url}
                            </a>
                          </>
                        )}
                      </p>
                    )}
                    {!section.description && section.link_url && (
                      <a
                        href={section.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xl text-[var(--text-link)] underline-offset-4 hover:underline"
                      >
                        {section.link_label || section.link_url}
                      </a>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <p className="text-xl font-normal leading-[1.5] text-muted-foreground text-pretty">
                <T
                  _str="Welcome to {company}'s customer portal! Please log in using your details below."
                  company={company.name || 'WasteHero'}
                />
              </p>
            )}
            {description && (
              <p className="text-base font-normal leading-[1.5] text-muted-foreground text-pretty">
                {description}
              </p>
            )}
          </div>

          <div
            className={cn('w-full', enter)}
            style={{ animationDelay: '160ms' }}
          >
            {children}
          </div>
        </section>

        <div
          className={cn(
            'flex flex-col items-center gap-8 text-xs font-medium uppercase leading-none tracking-[0.05em]',
            fadeEnter
          )}
          style={{ animationDelay: '240ms' }}
        >
          {/* Skip hire (PRODUCT-485): the public request door for visitors
              with no property — two tenant settings, the per-company
              skip-hire flag (PRODUCT-513) and the door's own; hidden unless
              both are on. A router Link (the door is a sibling route of the
              same BrowserRouter) so an iframe embed's basename
              (window.WH_SS_INIT_ROUTE) is kept. */}
          {footerLink !== undefined
            ? footerLink
            : skipHireEnabled &&
              company.crm_enable_customer_requests && (
                <LoginFooterLink to="/request" data-testid="request-door-link">
                  <T _str="New customer? Request a skip" />
                </LoginFooterLink>
              )}
          {hasMultipleLanguages && (
            <button
              type="button"
              onClick={() => setIsLangModalOpen(true)}
              className="relative -m-4 cursor-pointer p-4 uppercase tracking-[0.05em] text-[var(--text-link)] outline-hidden transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <T _str="Change language" />
            </button>
          )}
          <p className="text-[var(--text-muted)]">
            <T _str="Powered by WasteHero" />
          </p>
        </div>
      </div>

      {loginImageCss && isDesktop && (
        <div
          className="flex-1 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: loginImageCss }}
          aria-hidden
        />
      )}

      {hasMultipleLanguages && (
        <LanguagePickerModal
          open={isLangModalOpen}
          onOpenChange={setIsLangModalOpen}
        />
      )}
    </div>
  );
};
